// Sessions section for the event detail page.
// Mirrors SpeakerList — owns its own fetch + inline create/edit/delete state.

import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api, ApiError } from '../../lib/api';

const SESSION_TYPES = ['keynote', 'panel', 'workshop', 'roundtable', 'presentation', 'networking'];
const EMPTY_FORM = { time: '', title: '', speaker: '', type: '' };

function toForm(session) {
  return {
    time: session.time ?? '',
    title: session.title ?? '',
    speaker: session.speaker ?? '',
    type: session.type ?? '',
  };
}

function toRequestBody(form) {
  return {
    title: form.title.trim(),
    time: form.time.trim() || null,
    speaker: form.speaker.trim() || null,
    type: form.type || null,
  };
}

export function SessionList({ eventId, initial, readOnly }) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState(initial ?? []);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    api.sessions
      .list(eventId)
      .then((data) => {
        if (cancelled) return;
        setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[sessions] list failed, keeping initial:', err.message);
      });
    return () => { cancelled = true; };
  }, [eventId, readOnly]);

  function startAdd() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setAdding(true);
  }

  function startEdit(session) {
    setAdding(false);
    setEditingId(session.id);
    setForm(toForm(session));
    setError(null);
  }

  function cancel() {
    setAdding(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (submitting) return;
    if (!form.title.trim()) {
      setError(t('events.detail.sessionForm.titleRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (editingId) {
        const updated = await api.sessions.update(eventId, editingId, toRequestBody(form));
        setSessions((prev) => prev.map((s) => (s.id === editingId ? updated : s)));
      } else {
        const created = await api.sessions.create(eventId, toRequestBody(form));
        setSessions((prev) => [...prev, created]);
      }
      cancel();
    } catch (err) {
      console.error('[sessions] save failed:', err);
      setError(err instanceof ApiError ? err.message : t('events.detail.sessionForm.saveError'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(session) {
    if (!window.confirm(t('events.detail.sessionForm.confirmDelete', { title: session.title }))) return;
    try {
      await api.sessions.delete(eventId, session.id);
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
      if (editingId === session.id) cancel();
    } catch (err) {
      console.error('[sessions] delete failed:', err);
      window.alert(err instanceof ApiError ? err.message : t('events.detail.sessionForm.deleteError'));
    }
  }

  return (
    <section className="list-section">
      <header className="list-section__header">
        <h3 className="list-section__title">{t('events.detail.sections.sessions')}</h3>
        {!adding && !editingId && !readOnly && (
          <button type="button" className="list-section__add" onClick={startAdd}>
            {t('events.detail.add.session')}
          </button>
        )}
      </header>

      {adding && !editingId && (
        <SessionForm
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          onCancel={cancel}
          submitting={submitting}
          error={error}
          mode="create"
        />
      )}

      <ul>
        {sessions.map((session) =>
          editingId === session.id ? (
            <li key={session.id}>
              <SessionForm
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                onCancel={cancel}
                submitting={submitting}
                error={error}
                mode="edit"
                onDelete={() => handleDelete(session)}
              />
            </li>
          ) : (
            <li key={session.id} className="list-row">
              <button
                type="button"
                className="list-row__body list-row__body--button"
                onClick={() => !readOnly && startEdit(session)}
                disabled={readOnly}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: 1 }}
              >
                {session.time && <span className="list-row__time">{session.time}</span>}
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div className="list-row__primary">{session.title}</div>
                  {session.speaker && (
                    <div className="list-row__secondary">{session.speaker}</div>
                  )}
                </span>
                {session.type && (
                  <span className={`type-badge type-badge--${session.type}`}>
                    {t(`events.detail.sessionTypes.${session.type}`)}
                  </span>
                )}
              </button>
              {!readOnly && (
                <div className="list-row__actions">
                  <button
                    type="button"
                    className="list-row__action list-row__action--edit"
                    onClick={() => startEdit(session)}
                    aria-label={`${t('common.edit')} ${session.title}`}
                    title={t('common.edit')}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="list-row__action"
                    onClick={() => handleDelete(session)}
                    aria-label={`${t('common.remove')} ${session.title}`}
                    title={t('common.remove')}
                  >
                    ✕
                  </button>
                </div>
              )}
            </li>
          ),
        )}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline create/edit form
// ─────────────────────────────────────────────────────────────────────────────

function SessionForm({ form, setForm, onSubmit, onCancel, submitting, error, mode, onDelete }) {
  const { t } = useTranslation();

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <form className="inline-form" onSubmit={onSubmit}>
      <div className="inline-form__row">
        <div className="inline-form__field">
          <label className="inline-form__label">{t('events.detail.sessionForm.time')}</label>
          <input
            type="text"
            className="inline-form__control"
            value={form.time}
            placeholder={t('events.detail.sessionForm.timePlaceholder')}
            onChange={(e) => update('time', e.target.value)}
          />
        </div>
        <div className="inline-form__field">
          <label className="inline-form__label">{t('events.detail.sessionForm.type')}</label>
          <select
            className="inline-form__control"
            value={form.type}
            onChange={(e) => update('type', e.target.value)}
          >
            <option value="">{t('events.detail.sessionForm.typeNone')}</option>
            {SESSION_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`events.detail.sessionTypes.${type}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="inline-form__field">
        <label className="inline-form__label">{t('events.detail.sessionForm.title')}</label>
        <input
          type="text"
          className="inline-form__control"
          value={form.title}
          autoFocus
          onChange={(e) => update('title', e.target.value)}
        />
      </div>

      <div className="inline-form__field">
        <label className="inline-form__label">{t('events.detail.sessionForm.speaker')}</label>
        <input
          type="text"
          className="inline-form__control"
          value={form.speaker}
          onChange={(e) => update('speaker', e.target.value)}
        />
      </div>

      {error && <div className="inline-form__error">{error}</div>}

      <div className="inline-form__actions">
        {mode === 'edit' && onDelete && (
          <button
            type="button"
            className="button button--ghost inline-form__delete"
            onClick={onDelete}
            disabled={submitting}
          >
            {t('common.delete')}
          </button>
        )}
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="button button--primary" disabled={submitting}>
          {submitting
            ? t('common.saveSaving')
            : mode === 'edit'
              ? t('common.save')
              : t('events.detail.sessionForm.add')}
        </button>
      </div>
    </form>
  );
}
